import { ChangeDetectionStrategy, Component, computed, input, EventEmitter, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { FilledButtonComponent } from '@custom-components/general/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { PartnerUserModel } from '@core/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TableComponent } from '@custom-components/general/table/table.component';
import { COMPANY_USERS_TABLE_COLUMNS_CONFIG } from '@core/constants/columns-config.const';
import { COMPANY_USERS_PAGE_SIZE } from '@core/constants/company.constant';

@Component({
  selector: 'xemex-company-users-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    FilledButtonComponent,
    TranslateModule,
    TableComponent,
  ],
  templateUrl: './company-users-tab.component.html',
  styleUrl: './company-users-tab.component.scss',
})
export class CompanyUsersTabComponent {
  users = input<PartnerUserModel[]>([]);
  permissions = input<string[]>([]);
  isLoading = input<boolean>(false);
  totalUsers = input<number>(0);

  @Output() createUser = new EventEmitter<void>();
  @Output() editUser = new EventEmitter<{ userOid: string }>();
  @Output() deleteUser = new EventEmitter<{ userOid: string }>();
  @Output() resendInvite = new EventEmitter<{ userOid: string }>();
  @Output() pageChange = new EventEmitter<{ pageIndex: number; pageSize: number }>();

  protected readonly columns = COMPANY_USERS_TABLE_COLUMNS_CONFIG;
  protected readonly pageSize = COMPANY_USERS_PAGE_SIZE;

  protected readonly tableData = computed(
    () =>
      this.users().map(u => ({
        ...u,
        translatedRole: this.translate.instant('roles.roleNames.' + u.roleName.toLowerCase()),
        translatedStatus: this.translate.instant(
          u.status === 'Active' ? 'user.inviteAccepted' :
          u.status === 'Disabled' ? 'user.disabled' : 'user.invitePending'
        ),
      })) as unknown as Record<string, unknown>[],
  );

  protected hasAnyAction = computed(() =>
    this.permissions().some(p => ['edit_user_details', 'delete_users', 'create_new_users'].includes(p))
  );

  constructor(private readonly translate: TranslateService) {}

  protected onPageChange(event: PageEvent): void {
    this.pageChange.emit({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  onCreateUser() { this.createUser.emit(); }
  onEditUser(userOid: string) { this.editUser.emit({ userOid }); }
  onDeleteUser(userOid: string) { this.deleteUser.emit({ userOid }); }
  onResendInvite(userOid: string) { this.resendInvite.emit({ userOid }); }
}
